// @ts-strict-ignore
import debug from 'debug';

import { DebugLogsNamespace } from 'constants/debug-logs-namespace';
import { broadcastFreeAccessPage, broadcastInitError, broadcastInitialData } from 'entries/base/base';
import { type InitialData } from 'entries/base/interfaces/initial-data';
import { prepareApplicationCore } from 'entries/base/services/application-core/app';
import { fetchStartupData } from 'entries/base/services/initial-data/fetch-startup-data';
import { redirectToAccounts } from 'helpers/redirect-to-accounts';
import { getAccessToken, getExpiresIn, getScopes, isGhostLogin } from 'services/auth/auth-storage-manager';
import { resetStartupReauthAttempts } from 'services/server/startup-reauth-guard';
import { initStoreMetrics } from 'services/store-metrics/init';
import { injectOrganizationTrackingParams } from 'services/store-metrics/organization-tracking';

import { saveCredentials } from './helpers/credentials';

const log = debug(DebugLogsNamespace.AppStartup);

export async function start(): Promise<void> {
  /**
   * Save credentials to the cookies if they exist.
   * They are passed in the url hash by the accounts service.
   * We should save the credentials before doing anything else because we want to stay logged in even after a runtime error occurs.
   */
  saveCredentials();

  /**
   * Create application core `App` object.
   *
   * MUST stay in this function's synchronous prefix, before any `await`. `base`, `init` and
   * `app` are three `defer` scripts that execute in document order, so `app` evaluates the
   * moment `start()` first yields — and `source/config/setup.ts` calls `getApplicationCore()`
   * at module scope. Awaiting anything before this point throws
   * "Application core is not initialized" out of the `app` bundle.
   *
   * Runs unconditionally, including on the redirect path below that used to skip it.
   * `prepareExternalApi()` and `prepareDesktopAppEncapsulation()` only build closures over
   * event emitters, so this is free, and the desktop app wants `App.config` / `App.SmartClient`
   * as early as possible anyway.
   */
  prepareApplicationCore();

  log('Starting the application');
  log('Build info', { buildNumber: BUILD_NUMBER, buildTimestamp: BUILD_INFO_TIMESTAMP, commitHash: COMMIT_HASH });

  /**
   * Get the credentials if they are present.
   */
  const accessToken = getAccessToken();
  const scopes = getScopes() ?? [];
  const expiresIn = getExpiresIn();
  const isGhost = isGhostLogin();
  const isLoggedIn = !!accessToken;

  log('Credentials', { accessToken, expiresIn, scopes, isGhost, isLoggedIn });

  /**
   * Enrich the URL with the organization's signup attribution before store-metrics runs, so its
   * inbound pass — and GTM, which boots off the `store-metrics:ready` event that pass dispatches
   * — see the params on the first `page_view`. Self-gating, self-limiting and never throwing;
   * see `injectOrganizationTrackingParams`. AC-4434.
   *
   * Skipped synchronously for logged-out, ghost, and pending-corrective-redirect loads, which
   * are byte-for-byte unchanged. `deferTrackingBoot` marks the transient OAuth-callback load
   * that App.start() is about to replace — GA/GTM never boot there, so enriching would only
   * delay the redirect. AC-4320.
   */
  if (isLoggedIn && !isGhost && !window.deferTrackingBoot) {
    await injectOrganizationTrackingParams();
  }

  /**
   * Initialize cross-domain tracking (store-metrics). Runs on every load, regardless of
   * redirect/return, so inbound URL params are captured and outbound links are decorated.
   */
  initStoreMetrics();

  /**
   * Check if the user is on the logout screen or ticket rating screen.
   */
  const path = window.location.pathname;
  const isLogoutScreen = /\/keep-on-chatting-with-customers$/.test(path);
  const isTicketRatingScreen = /\/ticket_rating\/(.*)\/(.*)\/(.*)$/.test(path);
  const isFreeAccessScreen = isLogoutScreen || isTicketRatingScreen;

  log('Checking free access screen', { isLogoutScreen, isTicketRatingScreen });

  /**
   * We can quickly check that credentials are not present and redirect to accounts.
   */
  if (!isLoggedIn && !isFreeAccessScreen) {
    log('Redirecting to accounts');
    redirectToAccounts();

    return;
  }

  /**
   * Show logout screen or ticket rating screen for logged out users after refresh.
   * No data will be fetched in this case.
   */
  if (!isLoggedIn && isFreeAccessScreen) {
    broadcastFreeAccessPage(path);

    return;
  }

  /**
   * We are logged in by some credentials (not necessarily valid ones).
   * Try to fetch the most critical data.
   */
  const startupDataResult = await fetchStartupData(isGhost);

  log('Startup data fetch result', startupDataResult);

  if (startupDataResult.success === false) {
    /**
     * Handle the error by showing specific error page or redirecting to accounts.
     */
    broadcastInitError(startupDataResult);

    return;
  }

  const { startupData } = startupDataResult;

  const initialData: InitialData = {
    startupData,
    isGhost,
    accessToken,
    expiresIn,
    scopes,
  };

  /**
   * Startup succeeded — clear the re-auth loop guard so a future auth failure starts fresh.
   */
  resetStartupReauthAttempts();

  /**
   * All good for initialization. The license is ready to be used by logged in agent.
   */
  broadcastInitialData(initialData);
}

void start();
